<template>
    <div>
        <report-header
            :title="title"
            :isLoading="isLoading"
            :reportPagination="pagination"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />

        <b-card>
            <div id="report-body" class="w-100">
                <div v-if="showDummyTable">
                    <QTable
                        :columns="columns"
                        :data="dummyTableData"
                        hide-bottom
                        :loading="isLoading"
                        class="q-table"
                        :rows-per-page-options="[0]"
                    />
                </div>

                <div v-else v-for="(staff, i) in staffList" :key="i">
                    <p class="staff-name">
                        {{ staff.staff_name }}
                        <span v-if="!staff.isactive">(Inactive)</span>
                    </p>
                    <QTable
                        :columns="columns"
                        :data="staff.records || []"
                        hide-bottom
                        :loading="isLoading"
                        class="q-table"
                        :rows-per-page-options="[0]"
                    >
                        <template v-slot:body-cell-date="props">
                            <q-td>
                                {{ toDatetime(props.value) }}
                            </q-td>
                        </template>
                        <template v-slot:body-cell-clockIn="props">
                            <q-td>{{ convertClockIn(props.value) }}</q-td>
                        </template>
                        <template v-slot:body-cell-clockOut="props">
                            <q-td>{{ convertClockOut(props.row) }}</q-td>
                        </template>
                        <template v-slot:body-cell-adminNotes="props">
                            <q-td>
                                <div v-html="props.value"></div>
                            </q-td>
                        </template>
                        <template v-slot:bottom-row>
                            <q-tr>
                                <q-td colspan="3">
                                    <strong>Total:</strong>
                                </q-td>
                                <q-td>
                                    <strong>
                                        {{ staffTotal[staff.user_id] }}
                                    </strong>
                                </q-td>
                                <q-td colspan="2" />
                            </q-tr>

                            <!-- last table will include grand total row -->
                            <q-tr v-if="i === staffList.length - 1">
                                <q-td colspan="6"></q-td>
                            </q-tr>
                            <q-tr
                                v-if="i === staffList.length - 1"
                                class="totalRow"
                            >
                                <q-td colspan="3">
                                    <strong>Grand Total:</strong>
                                </q-td>
                                <q-td>
                                    <strong>
                                        {{ grandTotal.toFixed(2) }}
                                    </strong>
                                </q-td>
                                <q-td colspan="2" />
                            </q-tr>
                        </template>
                    </QTable>
                </div>
            </div>
        </b-card>
    </div>
</template>

<script>
import Report from "@/mixins/report.js";
import CREPORT from "@/constants/report";
import { QTable } from "quasar";
import "@/css/quasar-table.css";

const columnsDefinition = [
    {
        name: "date",
        field: "date",
        label: "Date",
        align: "left",
        headerStyle: "font-weight:bold; width:180px;"
    },
    {
        name: "clockIn",
        field: "clock_in",
        label: "Clock In",
        align: "left",
        headerStyle: "font-weight:bold; width:180px;"
    },
    {
        name: "clockOut",
        field: "clock_out",
        label: "Clock Out",
        align: "left",
        headerStyle: "font-weight:bold; width:180px;"
    },
    {
        name: "totalHours",
        field: "total_hours",
        label: "Total Hours",
        align: "left",
        headerStyle: "font-weight:bold; width:180px;"
    },
    {
        name: "adminNotes",
        field: "admin_notes",
        label: "Admin Notes",
        align: "left",
        headerStyle: "font-weight:bold"
    },
    {
        name: "staffNotes",
        field: "staff_notes",
        label: "Staff Notes",
        align: "left",
        headerStyle: "font-weight:bold"
    }
];

const PER_PAGE = 30;

export default {
    components: {
        QTable,
        ReportHeader: () => import("@/components/report/report-header")
    },
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getStartDate("week"),
                    end: this.getEndDate("day")
                };
            }
        }
    },
    data() {
        return {
            title: "Time Sheets",
            columns: columnsDefinition,
            showDummyTable: true,
            dummyTableData: [],
            staffList: [],
            isLoading: true,
            staffTotal: {},
            currentPage: 1,
            grandTotal: 0,
            dateRange:""
        };
    },
    computed: {
        startDate() {
            if (this.data.start) {
                return this.data.start.format(this.getDateFormat());
            } else {
                return null;
            }
        },
        endDate() {
            if (this.data.end) {
                return this.data.end.format(this.getDateFormat());
            } else {
                return null;
            }
        }
    },
    mixins: [Report],
    beforeDestroy: function () {
        this.$root.$off("report-filter-date-range:refresh", this.fetchData);
    },
    async mounted() {
        let self = this;

        await self._getSetting();
        self.data.exportOption = this._hasAccess("report.export");
        self.data.start = self.getStartDate("isoWeek");
        self.data.end = self.getEndDate("day");
        self.data.from = self.converToUnix(self.data.start);
        self.data.to = self.converToUnix(self.data.end);
        self.data.filterTitle = "Filter Time Sheets";
        self.data.isShowDateRange = true;

        self.fetchData();

        self.$root.$on("report-filter-date-range:refresh", self.fetchData);
    },
    methods: {
        async fetchData() {
            try {
                this.isLoading = true;
                this.showDummyTable = true;
                this.currentPage = 1;
                this.grandTotal = 0;
                this.staffTotal = {};
                this.dateRange = this.getDateRange(this.data.from, this.data.to);

                let res = await this.requestRemoteData(true);
                let response = res.data.response;

                if (response.message && response.message.code === 200) {
                    this.staffList = response.resource.header_title;

                    if (this.staffList.length > 0) {
                        this.proceedFetchRecords();
                    } else {
                        this.isLoading = false;
                        this.dummyTableData = [{ date: "No record available" }];
                    }
                } else {
                    this.isLoading = false;
                    throw new Error("Unable to fetch records");
                }
            } catch (error) {
                console.log(error);
            }
        },
        async requestRemoteData(showHeader, page = 1, perPage = PER_PAGE) {
            let showHeaderOption = {};
            let pageOptions = {};

            if (showHeader) {
                showHeaderOption = {
                    showHeader: true
                };
            } else {
                pageOptions = {
                    page: page,
                    perPage: perPage
                };
            }

            const params = {
                from: this.data.start.unix(),
                to: this.data.end.unix(),
                ...showHeaderOption,
                ...pageOptions
            };

            if (this.data.isIncludedInactive) {
                params["included_inactive"] = true;
                this.$store.commit(
                    "report/setTimesheetsIsIncludedInactive",
                    true
                );
            } else {
                this.$store.commit(
                    "report/setTimesheetsIsIncludedInactive",
                    false
                );
            }

            return await this.$http.get(
                `${process.env.BARRAMUNDI}report/reports/${CREPORT.TYPE.TIME_SHEETS}`,
                {
                    params
                }
            );
        },
        async proceedFetchRecords() {
            this.showDummyTable = false;

            let lastPage = false;

            // fetch records + continue fetch until last page.
            while (!lastPage) {
                let res = await this.requestRemoteData(
                    false,
                    this.currentPage,
                    PER_PAGE
                );

                let response = res.data.response;
                let records = [];

                if (response.message.code === 200) {
                    records = response.resource.results || [];
                }

                // loop staff
                for (let staff of this.staffList) {
                    // insert records
                    let staffRecords = records.filter(
                        rec => rec.staff_id === staff.user_id
                    );

                    // append with current records (if any)
                    staff.records = staff.records || [];
                    staff.records = [...staff.records, ...staffRecords];
                }

                // force render right after append
                this.$forceUpdate();

                // detect last page
                if (records.length < PER_PAGE) {
                    lastPage = true;
                } else {
                    this.currentPage += 1;
                }
            }

            this.calculateTotal();
            this.isLoading = false;
        },
        calculateTotal() {
            let totals = {};
            let grandTotal = 0;

            for (let staff of this.staffList) {
                if (!totals[staff.user_id]) {
                    totals[staff.user_id] = 0;
                }

                for (let record of staff.records) {
                    // total hours
                    let totalHours = Number(record.total_hours);

                    if (isNaN(totalHours)) {
                        totalHours = 0;
                    } else {
                        totals[staff.user_id] += totalHours;
                    }
                }

                totals[staff.user_id] = totals[staff.user_id].toFixed(2);
            }

            this.staffTotal = totals;

            // grand total
            for (let staffId in totals) {
                grandTotal += Number(totals[staffId]);
            }

            this.grandTotal = grandTotal;
        },
        convertClockIn(clockIn) {
            let clockInTime = this._toDateTime(parseFloat(clockIn)).toFormat(
                "hh:mm a"
            );
            if (clockInTime == "12:00 AM") {
                return "Split";
            }
            return clockInTime;
        },
        convertClockOut(row) {
            let clockInDate = this.toDatetime(row.clock_in);
            let clockOutDate = this.toDatetime(row.clock_out);

            let clockOutTime = this._toDateTime(
                parseFloat(row.clock_out)
            ).toFormat("hh:mm a");

            if (clockOutTime == "12:00 AM" && clockInDate != clockOutDate) {
                return "Split";
            }
            return clockOutTime;
        },
        toDatetime(timestamp) {
            return this._toDateTime(parseFloat(timestamp)).toFormat(
                this._getLuxonFormat()
            );
        }
    }
};
</script>

<style scoped>
.report-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 5px;
}
.report-heading .period {
    font-size: 0.7em;
}
th {
    font-weight: bold;
    white-space: nowrap;
}
p.staff-name {
    font-weight: bold;
    font-size: 1.2em;
    margin: 20px 0 0 0;
    padding: 5px 15px;
    padding-left: 0;
    border-bottom: 1px solid #c6c6c6;
}
.q-table {
    margin-bottom: 40px;
}
.totalRow td {
    font-weight: bold;
    border-top: 3px double #e4e4e4;
}
</style>
